import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchGoalById,
  fetchGoals,
} from '../../../store/landscapeAPI/goal/reducers/goal.slice';
import { fetchIndicatorGoalsByIndicatorId } from '../../../store/landscapeAPI/indicator/reducers/indicatorGoal.slice';
import {
  deleteLandscapeIndicator,
  saveLandscapeIndicator,
  updateLandscapeIndicator,
} from '../../../store/landscapeAPI/landscape/reducers/landscape-indicator.slice';
import { fetchLandscapeById } from '../../../store/landscapeAPI/landscape/reducers/landscape.slice';
import { AppDispatch, RootState } from '../../../store/store';
import { ListItem } from '../../atoms/SortableListItem/SortableListItem';
import Spinner from '../../atoms/Spinner/Spinner';
import { LandscapeIndicatorsTemplate } from '../../templates';
import { LocalesLanguagesConstants } from '../../../locales/locales';
import cn from 'classnames';
import styles from './LandscapeIndicatorsPage.module.css';
import { AddIndicatorModal } from '../../organisms';
import { getUUID } from '../../../utils/UUID';
import { IndicatorResponseDto } from '../../../store/landscapeAPI/indicator/dto/response/indicator-response.dto';

interface LandscapeIndicatorsPageProps {}

const LandscapeIndicatorsPage: FC<LandscapeIndicatorsPageProps> = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const dispatch = useDispatch<AppDispatch>();

  const { landscape, status: landscapeStatus } = useSelector(
    (state: RootState) => state.landscapeSlice
  );
  const { goals, status: goalsStatus } = useSelector(
    (state: RootState) => state.goalSlice
  );
  const { status: landscapeIndicatorStatus } = useSelector(
    (state: RootState) => state.landscapeIndicatorSlice
  );

  const [data, setData] = useState<ListItem[]>();
  const [changeOrderEnabled, setChangeOrderEnabled] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedGoal, setSelectedGoal] = useState<string>();
  const [selectedIndicator, setSelectedIndicator] = useState<string>();
  const [description, setDescription] = useState<string>('');
  const [indicators, setIndicators] = useState<IndicatorResponseDto[]>();

  useEffect(() => {
    dispatch(
      fetchLandscapeById({ id: id!, language: LocalesLanguagesConstants.en })
    );
    dispatch(fetchGoals());
  }, [dispatch, id]);

  useEffect(() => {
    if (!selectedGoal) return;
    dispatch(
      fetchGoalById({
        id: selectedGoal,
        language: LocalesLanguagesConstants.en,
      })
    )
      .unwrap()
      .then((resp) => setIndicators(resp.indicators));
  }, [selectedGoal, dispatch]);

  useEffect(() => {
    (async () => {
      if (
        landscape &&
        landscapeStatus === 'succeeded' &&
        goals &&
        goalsStatus === 'succeeded'
      ) {
        const temp: any[] = [];

        for (let i = 0; i < landscape.indicators.length; i++) {
          await dispatch(
            fetchIndicatorGoalsByIndicatorId(landscape.indicators[i].id)
          )
            .unwrap()
            .then((resp) => {
              if (resp.length > 0) {
                const tempGoal = goals.find(
                  (item) => item.id === resp[0].goalId
                );
                const tempIndex = temp.findIndex(
                  (item) => item.id === resp[0].goalId
                );
                if (tempIndex === -1 && tempGoal) {
                  temp.push({
                    id: tempGoal.id,
                    title: tempGoal.name,
                    sortOrder: temp.length,
                    subListItems: [
                      {
                        compactIndicatorId:
                          landscape.indicators[i].compactIndicatorid,
                        id: landscape.indicators[i].id,
                        title: landscape.indicators[i].name,
                        sortOrder: landscape.indicators[i].sortOrder,
                        description: landscape.indicators[i].description,
                      },
                    ],
                  });
                } else {
                  temp[tempIndex].subListItems.push({
                    compactIndicatorId:
                      landscape.indicators[i].compactIndicatorid,
                    id: landscape.indicators[i].id,
                    title: landscape.indicators[i].name,
                    sortOrder: landscape.indicators[i].sortOrder,
                    description: landscape.indicators[i].description,
                  });
                }
              }
            });
        }
        setData(temp);
      }
    })();
  }, [landscape, landscapeStatus, goals, goalsStatus, dispatch]);

  const onReorder = (items: any[], from: number, to: number) => {
    setData(items);

    if (landscape) {
      const firstInd =
        landscape.indicators.findIndex((item) => item.sortOrder === from) ?? -1;
      const secondInd =
        landscape.indicators.findIndex((item) => item.sortOrder === to) ?? -1;

      if (firstInd !== -1 && secondInd !== -1) {
        dispatch(
          updateLandscapeIndicator({
            id: landscape.indicators[firstInd].compactIndicatorid,
            indicatorId: landscape.indicators[firstInd].id,
            compactId: landscape.id,
            sortOrder: to,
            published: true,
            description: landscape.indicators[firstInd].description,
          })
        );
        dispatch(
          updateLandscapeIndicator({
            id: landscape.indicators[secondInd].compactIndicatorid,
            indicatorId: landscape.indicators[secondInd].id,
            compactId: landscape.id,
            sortOrder: from,
            published: true,
            description: landscape.indicators[firstInd].description,
          })
        );
      }
    }
  };

  const callbackFn = async () => {
    if (!selectedIndicator || !id) return;
    await dispatch(
      saveLandscapeIndicator({
        compactId: id,
        indicatorId: selectedIndicator,
        id: getUUID(),
        published: true,
        description: description,
      })
    );
    dispatch(
      fetchLandscapeById({ id: id!, language: LocalesLanguagesConstants.en })
    );
    setShowModal(false);
  };

  const handleEdit = (landscapeIndicatorId: string) => {
    navigate(
      `/landscapes/edit/${id}/indicators/edit/${landscapeIndicatorId}/description`
    );
  };

  const handleDelete = async (landscapeIndicatorId: string) => {
    await dispatch(deleteLandscapeIndicator(landscapeIndicatorId)).unwrap();
    dispatch(
      fetchLandscapeById({ id: id!, language: LocalesLanguagesConstants.en })
    );
  };

  if (
    landscapeStatus === 'loading' ||
    goalsStatus === 'loading' ||
    landscapeIndicatorStatus === 'loading' ||
    !data
  ) {
    return <Spinner />;
  }

  return (
    <div className={cn(styles.wrapper)}>
      {showModal && (
        <AddIndicatorModal
          show={showModal}
          setShow={setShowModal}
          goals={goals ?? []}
          indicators={indicators ?? []}
          setDescription={setDescription}
          setSelectedGoal={setSelectedGoal}
          selectedGoal={selectedGoal}
          setSelectedIndicator={setSelectedIndicator}
          callbackFn={callbackFn}
        />
      )}
      <LandscapeIndicatorsTemplate
        setShow={setShowModal}
        listItems={data}
        setListItems={onReorder}
        changeOrderEnabled={changeOrderEnabled}
        setChangeOrderEnabled={setChangeOrderEnabled}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
      />
    </div>
  );
};

export default LandscapeIndicatorsPage;
